import React, { useEffect, useMemo, useState, Fragment, Component } from 'react';
import { Package, Clock, CheckCircle, XCircle, Search, AlertTriangle, X, Send, ChevronRight, MapPin, Calendar, MessageSquare, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOrders } from '../contexts/OrderContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { supabaseClient } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { displayProductName, formatCurrency } from '../utils/formatters';
const CLAIM_TYPES = ['Producto incorrecto', 'Producto en mal estado', 'Pedido incompleto', 'Demora excesiva', 'Cobro incorrecto', 'Otro'];
export function MyOrdersPage() {
  useDocumentTitle('Mis Pedidos');
  const {
    orders,
    loading
  } = useOrders();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'Activos' | 'Entregados' | 'Todos'>('Activos');
  // Claim State
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimOrder, setClaimOrder] = useState<any>(null);
  const [claimType, setClaimType] = useState(CLAIM_TYPES[0]);
  const [claimDescription, setClaimDescription] = useState('');
  const [claimPriority, setClaimPriority] = useState('Media');
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [myClaims, setMyClaims] = useState<any[]>([]);
  const [claimSuccess, setClaimSuccess] = useState(false);
  // Detectar retorno exitoso de Azul
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status === 'success') {
      showToast('success', '¡Pago Exitoso!', 'Tu pedido con Azul fue procesado correctamente.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  // Load user's claims & Subscribe to Real-time changes
  useEffect(() => {
    const loadClaims = async () => {
      try {
        const allClaims = await supabaseClient.getClaims();
        const userClaims = allClaims.filter((c: any) => c.createdById === user?.id);
        setMyClaims(userClaims);
      } catch (e) {
        console.error('Error loading claims:', e);
      }
    };
    if (user?.id) {
      loadClaims();
      // Real-time subscription for claims
      const subscription = supabaseClient.subscribeToClaims(() => {
        loadClaims();
      });
      return () => {
        subscription?.unsubscribe();
      };
    }
  }, [user?.id]);
  const myOrders = useMemo(() => {
    if (!user) return [];
    return orders.filter(order => order.employeeId === user.id).sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
  }, [orders, user]);
  const filteredOrders = useMemo(() => {
    return myOrders.filter(order => {
      // Tab filter
      if (activeTab === 'Activos') {
        if (order.status === 'Entregado' || order.status === 'Cancelado') return false;
      } else if (activeTab === 'Entregados') {
        if (order.status !== 'Entregado') return false;
      }
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return order.orderNo.toLowerCase().includes(term) || order.product.toLowerCase().includes(term);
      }
      return true;
    });
  }, [myOrders, activeTab, searchTerm]);
  const counts = {
    Activos: myOrders.filter(o => o.status !== 'Entregado' && o.status !== 'Cancelado').length,
    Entregados: myOrders.filter(o => o.status === 'Entregado').length,
    Todos: myOrders.length
  };
  const getClaimForOrder = (orderId: string) => {
    return myClaims.find(c => c.orderId === orderId);
  };
  const handleOpenClaim = (order: any) => {
    setClaimOrder(order);
    setClaimType(CLAIM_TYPES[0]);
    setClaimDescription('');
    setClaimPriority('Media');
    setClaimSuccess(false);
    setShowClaimModal(true);
  };
  const handleSubmitClaim = async () => {
    if (!claimOrder || !user || !claimDescription.trim()) {
      showToast('warning', 'Campos requeridos', 'Describe tu reclamación');
      return;
    }
    setSubmittingClaim(true);
    try {
      await supabaseClient.createClaim({
        orderId: claimOrder.id,
        orderNo: claimOrder.orderNo,
        customerName: user.name,
        company: claimOrder.company,
        branch: claimOrder.branch,
        type: claimType,
        status: 'Pendiente',
        priority: claimPriority,
        description: claimDescription.trim(),
        createdBy: user.name,
        createdById: user.id
      });
      // Show success state inside modal instead of closing immediately
      setClaimSuccess(true);
      // Refresh claims list happens via subscription automatically, but we can force update local state if needed
      // The subscription will handle it.
    } catch (e) {
      console.error('Error creating claim:', e);
      showToast('error', 'Error', 'No se pudo enviar la reclamación');
      setSubmittingClaim(false);
    }
  };
  const closeClaimModal = () => {
    setShowClaimModal(false);
    setClaimOrder(null);
    setSubmittingClaim(false);
    setClaimSuccess(false);
  };
  // --- Components ---
  const MyOrderCard = ({
    order
  }: {
    order: any;
  }) => {
    const existingClaim = getClaimForOrder(order.id);
    const steps = ['Pendiente', 'En Preparación', 'Listo', 'Entregado'];
    const currentIdx = steps.indexOf(order.status);
    const isCancelled = order.status === 'Cancelado';
    const statusConfig: Record<string, {
      color: string;
      bg: string;
      border: string;
      dot: string;
    }> = {
      Pendiente: {
        color: 'text-amber-700 dark:text-amber-400',
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        dot: 'bg-amber-400'
      },
      'En Preparación': {
        color: 'text-blue-700 dark:text-blue-400',
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        dot: 'bg-blue-500'
      },
      Listo: {
        color: 'text-emerald-700 dark:text-emerald-400',
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        border: 'border-emerald-200 dark:border-emerald-800',
        dot: 'bg-emerald-500'
      },
      Entregado: {
        color: 'text-gray-500 dark:text-gray-400',
        bg: 'bg-gray-100 dark:bg-gray-800',
        border: 'border-gray-200 dark:border-gray-700',
        dot: 'bg-gray-400'
      },
      Cancelado: {
        color: 'text-red-700 dark:text-red-400',
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-800',
        dot: 'bg-red-500'
      }
    };
    const sc = statusConfig[order.status] || statusConfig['Pendiente'];
    const accentBorder: Record<string, string> = {
      Pendiente: 'border-l-amber-400',
      'En Preparación': 'border-l-blue-500',
      Listo: 'border-l-emerald-500',
      Entregado: 'border-l-gray-300 dark:border-l-gray-600',
      Cancelado: 'border-l-red-400'
    };
    return <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 border-l-4 ${accentBorder[order.status] || accentBorder['Pendiente']} overflow-hidden shadow-sm hover:shadow-md transition-shadow`} data-id="element-4830">
        <div className="p-4" data-id="element-4831">
          {/* Header row: order no + date + status */}
          <div className="flex items-center justify-between gap-2 mb-3" data-id="element-4832">
            <div className="flex items-center gap-2 min-w-0" data-id="element-4833">
              <span className="font-mono text-xs font-bold text-gray-400 dark:text-gray-500 shrink-0" data-id="element-4834">
                #{order.orderNo}
              </span>
              <span className="text-gray-200 dark:text-gray-700" data-id="element-4835">·</span>
              <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 truncate" data-id="element-4836">
                <Calendar size={10} className="shrink-0" data-id="element-4837" />
                {order.orderDate.split(',')[0]}
              </span>
            </div>
            <span className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${sc.color} ${sc.bg} ${sc.border}`} data-id="element-4838">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sc.dot} ${order.status === 'En Preparación' ? 'animate-pulse' : ''}`} data-id="element-4839" />
              {order.status}
            </span>
          </div>

          {/* Product name */}
          <h3 className="font-bold text-[15px] text-gray-900 dark:text-white leading-snug mb-1.5" data-id="element-4840">
            {displayProductName(order.product)}
          </h3>

          {/* Company + branch */}
          <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 mb-4" data-id="element-4841">
            <MapPin size={11} className="shrink-0" data-id="element-4842" />
            <span className="truncate" data-id="element-4843">{order.company}</span>
            {order.branch && <>
                <span className="text-gray-200 dark:text-gray-700" data-id="element-4844">·</span>
                <span className="truncate" data-id="element-4845">{order.branch}</span>
              </>}
          </div>

          {/* Price + Progress tracker */}
          <div className="flex items-end justify-between gap-4" data-id="element-4846">
            <div data-id="element-4847">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5" data-id="element-4848">
                Total
              </p>
              <span className="font-bold text-xl text-gray-900 dark:text-white tabular-nums" data-id="element-4849">
                {formatCurrency(order.total)}
              </span>
            </div>

            {isCancelled ? <div className="flex items-center gap-1.5 text-red-500 text-xs font-bold bg-red-50 dark:bg-red-900/20 px-3 py-1.5 rounded-xl border border-red-200 dark:border-red-800" data-id="element-4850">
                <XCircle size={13} data-id="element-4851" /> Cancelado
              </div> : <div className="flex-1 max-w-[200px]" data-id="element-4852">
                {/* Step dots with labels */}
                <div className="flex items-center gap-0.5 mb-1.5" data-id="element-4853">
                  {steps.map((step, idx) => {
                const done = currentIdx >= idx;
                const current = currentIdx === idx;
                return <Fragment key={step} data-id="element-4854">
                        <div title={step} className={`w-2.5 h-2.5 rounded-full shrink-0 transition-all duration-300
                            ${done ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'}
                            ${current ? 'ring-2 ring-emerald-300 dark:ring-emerald-800 scale-125' : ''}
                          `} data-id="element-4855" />
                        {idx < steps.length - 1 && <div className={`flex-1 h-0.5 rounded-full transition-all duration-300 ${currentIdx > idx ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-4856" />}
                      </Fragment>;
              })}
                </div>
                <p className="text-right text-[10px] font-semibold text-gray-400 dark:text-gray-500" data-id="element-4857">
                  {order.status === 'Entregado' ? '✓ Completado' : order.status}
                </p>
              </div>}
          </div>
        </div>

        {/* Claim footer - REDESIGNED */}
        {existingClaim ? <div className={`px-4 py-3 border-t flex flex-col gap-2 ${existingClaim.status === 'Aceptada' ? 'bg-emerald-50/50 border-emerald-100 dark:bg-emerald-900/10 dark:border-emerald-900/30' : existingClaim.status === 'Rechazada' ? 'bg-red-50/50 border-red-100 dark:bg-red-900/10 dark:border-red-900/30' : 'bg-amber-50/50 border-amber-100 dark:bg-amber-900/10 dark:border-amber-900/30'}`} data-id="element-4858">
            <div className="flex items-center justify-between" data-id="element-4859">
              <div className="flex items-center gap-2 font-bold text-sm" data-id="element-4860">
                {existingClaim.status === 'Aceptada' ? <CheckCircle size={16} className="text-emerald-600" data-id="element-4861" /> : existingClaim.status === 'Rechazada' ? <XCircle size={16} className="text-red-600" data-id="element-4862" /> : <Clock size={16} className="text-amber-600" data-id="element-4863" />}
                <span className={existingClaim.status === 'Aceptada' ? 'text-emerald-700 dark:text-emerald-400' : existingClaim.status === 'Rechazada' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'} data-id="element-4864">
                  Reclamación {existingClaim.status}
                </span>
              </div>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold" data-id="element-4865">
                {existingClaim.type}
              </span>
            </div>

            {/* Show resolution if available */}
            {existingClaim.resolution && <div className="text-xs text-gray-600 dark:text-gray-300 bg-white/50 dark:bg-black/20 p-2 rounded-lg border border-black/5 dark:border-white/5" data-id="element-4866">
                <span className="font-bold" data-id="element-4867">Respuesta:</span>{' '}
                {existingClaim.resolution}
              </div>}
          </div> : order.status === 'Entregado' ? <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center" data-id="element-4868">
            <span className="text-xs text-gray-400" data-id="element-4869">¿Hubo algún problema?</span>
            <button onClick={() => handleOpenClaim(order)} className="text-xs font-bold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-3 py-1.5 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors flex items-center gap-1.5" data-id="element-4870">
              <AlertTriangle size={12} data-id="element-4871" /> Reportar problema
            </button>
          </div> : null}
      </motion.div>;
  };
  return <div className="bg-gray-50 dark:bg-gray-950 min-h-screen pb-24" data-id="element-4872">
      <div className="max-w-2xl mx-auto px-4 pt-5 md:pt-7" data-id="element-4873">
        {/* Tabs + Search in one compact row on mobile */}
        <div className="mb-4 space-y-3" data-id="element-4874">
          {/* Tabs */}
          <div className="flex p-1 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-sm" data-id="element-4875">
            {(['Activos', 'Entregados', 'Todos'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2 px-2 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5
                  ${activeTab === tab ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`} data-id="element-4876">
                {tab}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold tabular-nums
                  ${activeTab === tab ? 'bg-white/20 dark:bg-black/20 text-white dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`} data-id="element-4877">
                  {counts[tab]}
                </span>
              </button>)}
          </div>

          {/* Search */}
          <div className="relative" data-id="element-4878">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-4879" />
            <input type="text" placeholder="Buscar orden o producto..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-100 dark:border-gray-800 rounded-xl bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 shadow-sm" data-id="element-4880" />
          </div>
        </div>

        {/* Content */}
        {loading ? <div className="flex flex-col items-center justify-center py-20 gap-3" data-id="element-4881">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-900 dark:border-t-white rounded-full animate-spin" data-id="element-4882"></div>
            <p className="text-sm text-gray-400" data-id="element-4883">Cargando pedidos...</p>
          </div> : filteredOrders.length === 0 ? <div className="text-center py-16 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm" data-id="element-4884">
            <div className="w-14 h-14 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-4885">
              <Package className="w-7 h-7 text-gray-300 dark:text-gray-600" data-id="element-4886" />
            </div>
            <h3 className="font-bold text-gray-900 dark:text-white mb-1" data-id="element-4887">
              Sin pedidos {activeTab.toLowerCase()}
            </h3>
            <p className="text-sm text-gray-400 dark:text-gray-500" data-id="element-4888">
              {activeTab === 'Activos' ? 'No tienes pedidos en curso.' : 'Tus pedidos aparecerán aquí.'}
            </p>
          </div> : <div className="space-y-3" data-id="element-4889">
            {filteredOrders.map(order => <MyOrderCard key={order.id} order={order} data-id="element-4890" />)}
          </div>}
      </div>

      {/* Claim Modal - REDESIGNED */}
      <AnimatePresence data-id="element-4891">
        {showClaimModal && claimOrder && <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" data-id="element-4892">
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 overflow-hidden max-h-[90vh] flex flex-col" data-id="element-4893">
              {claimSuccess ? <div className="p-10 flex flex-col items-center text-center" data-id="element-4894">
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 text-green-600 dark:text-green-400" data-id="element-4895">
                    <CheckCircle size={40} data-id="element-4896" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-4897">
                    ¡Reclamación Enviada!
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400 mb-8" data-id="element-4898">
                    Hemos recibido tu reporte. Te notificaremos tan pronto sea
                    revisado por el equipo administrativo.
                  </p>
                  <Button onClick={closeClaimModal} fullWidth className="bg-gray-900 text-white hover:bg-black" data-id="element-4899">
                    Entendido
                  </Button>
                </div> : <>
                  <div className="p-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50" data-id="element-4900">
                    <div className="flex items-center gap-3" data-id="element-4901">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-xl" data-id="element-4902">
                        <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-4903" />
                      </div>
                      <div data-id="element-4904">
                        <h2 className="font-bold text-gray-900 dark:text-white text-lg" data-id="element-4905">
                          Reportar Problema
                        </h2>
                        <p className="text-xs text-gray-500" data-id="element-4906">
                          Orden #{claimOrder.orderNo}
                        </p>
                      </div>
                    </div>
                    <button onClick={closeClaimModal} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 transition-colors" data-id="element-4907">
                      <X size={20} data-id="element-4908" />
                    </button>
                  </div>

                  <div className="p-5 space-y-6 overflow-y-auto" data-id="element-4909">
                    {/* Order Summary */}
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex gap-4" data-id="element-4910">
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center shrink-0" data-id="element-4911">
                        <Package size={20} className="text-gray-400" data-id="element-4912" />
                      </div>
                      <div data-id="element-4913">
                        <p className="font-bold text-sm text-gray-900 dark:text-white line-clamp-1" data-id="element-4914">
                          {displayProductName(claimOrder.product)}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-4915">
                          {formatCurrency(claimOrder.total)} •{' '}
                          {claimOrder.company}
                        </p>
                      </div>
                    </div>

                    {/* Claim Type Pills */}
                    <div data-id="element-4916">
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5" data-id="element-4917">
                        ¿Qué sucedió?
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-4918">
                        {CLAIM_TYPES.map(type => <button key={type} onClick={() => setClaimType(type)} className={`px-3 py-2 rounded-lg text-xs font-bold border transition-all
                              ${claimType === type ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-black dark:border-white shadow-md transform scale-105' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'}
                            `} data-id="element-4919">
                            {type}
                          </button>)}
                      </div>
                    </div>

                    {/* Priority Toggle */}
                    <div data-id="element-4920">
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5" data-id="element-4921">
                        Prioridad
                      </label>
                      <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-xl" data-id="element-4922">
                        {['Baja', 'Media', 'Alta'].map(p => <button key={p} onClick={() => setClaimPriority(p)} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all
                              ${claimPriority === p ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}
                            `} data-id="element-4923">
                            {p}
                          </button>)}
                      </div>
                    </div>

                    {/* Description */}
                    <div data-id="element-4924">
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5" data-id="element-4925">
                        Detalles adicionales{' '}
                        <span className="text-red-500" data-id="element-4926">*</span>
                      </label>
                      <div className="relative" data-id="element-4927">
                        <textarea value={claimDescription} onChange={e => setClaimDescription(e.target.value)} rows={4} placeholder="Por favor describe el problema con el mayor detalle posible..." className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white dark:focus:bg-gray-900 transition-all resize-none" data-id="element-4928" />
                        <div className="absolute bottom-3 right-3 text-[10px] text-gray-400 font-bold" data-id="element-4929">
                          {claimDescription.length} caracteres
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900" data-id="element-4930">
                    <Button onClick={handleSubmitClaim} fullWidth disabled={submittingClaim || !claimDescription.trim()} className="bg-orange-600 hover:bg-orange-700 text-white border-none h-12 text-base shadow-lg shadow-orange-600/20" data-id="element-4931">
                      {submittingClaim ? <span className="flex items-center gap-2" data-id="element-4932">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-4933" />
                          Enviando...
                        </span> : <span className="flex items-center gap-2" data-id="element-4934">
                          Enviar Reporte <Send size={16} data-id="element-4935" />
                        </span>}
                    </Button>
                  </div>
                </>}
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}